// ClubCreationWizard Component
// Multi-step wizard for creating new clubs

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useUser } from '../../context/UserContext';
import { useNotification } from '../../context/NotificationContext';
import { auth } from '../../firebaseConfig';
import styles from './ClubCreationWizard.module.css';
import ClubCreationProgress from './ClubCreationProgress';
import { useSports } from '../../hooks/useSports';

// Import step components
import ClubDetailsStep from './steps/ClubDetailsStep';
import ContactInfoStep from './steps/ContactInfoStep';
import CategoriesStep from './steps/CategoriesStep';
import NotificationsStep from './steps/NotificationsStep';
import CompletionStep from './steps/CompletionStep';

// Default categories for new clubs
const DEFAULT_CATEGORIES = [
  { id: '1', name: 'Recreational', description: 'Fun, casual programs for all skill levels', enabled: true, isDefault: true },
  { id: '2', name: 'Competitive', description: 'Competitive training and tournament preparation', enabled: true, isDefault: true },
  { id: '3', name: 'Academy', description: 'Structured training programs with skill progression', enabled: true, isDefault: true },
  { id: '4', name: 'Leagues', description: 'Organized league play and scheduled competitions', enabled: true, isDefault: true },
  { id: '5', name: 'Camps', description: 'Short-term intensive training camps', enabled: true, isDefault: true },
  { id: '6', name: 'Trainings', description: 'Skill-specific training sessions', enabled: true, isDefault: true }
];

const ClubCreationWizard = ({ isOpen, onClose, onComplete, userLocation }) => {
  const { userProfile, refreshUserClubs } = useUser();
  const { showSuccess, showError } = useNotification();
  const { sports, sportNames, loading: sportsLoading } = useSports();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdClub, setCreatedClub] = useState(null);
  
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
  const totalSteps = 5;

  // Form data for all steps
  const [formData, setFormData] = useState({
    // Step 1: Club Details
    name: '',
    sport: '',
    type: 'Club',
    businessType: 'Public club',
    description: '',
    membershipType: 'free_open',
    membershipFee: 0,
    clubLogo: null, // Will be { url, publicId, transformedUrls } after upload
    
    // Branding (new fields)
    bannerImage: null, // Will be { url, publicId, transformedUrls } after upload
    primaryColor: null, // Hex color code
    tagline: null, // Short motto/tagline
    socialLinks: {
      website: null,
      facebook: null,
      instagram: null,
      twitter: null,
      youtube: null
    },

    // Step 2: Contact Info
    clubType: 'Physical Location',
    contactNumber: '',
    email: '',
    street: '',
    city: '',
    state: '',
    country: '',
    zipCode: '',
    location: userLocation ? {
      type: 'Point',
      coordinates: [userLocation.lng, userLocation.lat]
    } : null,

    // Step 3: Categories
    categories: [...DEFAULT_CATEGORIES],

    // Step 4: Notifications
    notifications: {
      channels: { inApp: true, email: true },
      categories: {
        program: true,
        event: true,
        rsvp: true,
        equipment: true,
        team: true,
        assignment: true
      }
    }
  });

  // Update form data
  const updateFormData = (updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  // Validate current step
  const validateStep = (step) => {
    switch (step) {
      case 1:
        return (
          formData.name.trim().length >= 3 &&
          formData.sport !== ''
        );
      
      case 2:
        if (formData.clubType === 'Physical Location') {
          return (
            formData.contactNumber.trim() !== '' &&
            formData.street.trim() !== '' &&
            formData.city.trim() !== '' &&
            formData.state.trim() !== '' &&
            formData.zipCode.trim() !== ''
          );
        } else {
          return (
            formData.contactNumber.trim() !== '' &&
            formData.city.trim() !== ''
          );
        }
      
      case 3:
        // At least one category must be enabled
        return formData.categories.some(cat => cat.enabled);
      
      case 4:
        // Info step - always valid
        return true;
      
      case 5:
        // Completion - no validation
        return true;
      
      default:
        return false;
    }
  };

  // Handle Next button
  const handleNext = () => {
    if (!validateStep(currentStep)) {
      if (currentStep === 1) {
        showError('Please fill in club name (min 3 characters) and select a sport.');
      } else if (currentStep === 2) {
        showError('Please fill in all required contact information.');
      } else if (currentStep === 3) {
        showError('Please enable at least one category.');
      }
      return;
    }

    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  // Handle Back button
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Handle final submission
  const handleSubmit = async () => {
    if (!validateStep(currentStep)) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Get Firebase token
      const token = await auth.currentUser?.getIdToken();

      // 1. Create Club
      // Ensure we have a valid location - use formData.location or userLocation as fallback
      const location = formData.location || (userLocation ? {
        type: 'Point',
        coordinates: [userLocation.lng, userLocation.lat]
      } : null);

      const clubPayload = {
        name: formData.name,
        sport: formData.sport,
        type: formData.type,
        businessType: formData.businessType,
        description: formData.description || '',
        membershipType: formData.membershipType,
        membershipFee: formData.membershipType === 'paid' ? formData.membershipFee : 0,
        clubLogo: formData.clubLogo?.url || formData.clubLogo || '',
        // Branding fields
        bannerImage: formData.bannerImage || null,
        primaryColor: formData.primaryColor || null,
        tagline: formData.tagline || null,
        socialLinks: formData.socialLinks || {},
        // Contact info
        clubType: formData.clubType,
        contactNumber: formData.contactNumber,
        email: formData.email || '',
        street: formData.street || '',
        city: formData.city,
        state: formData.state || '',
        country: formData.country || '',
        zipCode: formData.zipCode || '',
        location: location,
        createdBy: userProfile?._id
      };

      const clubResponse = await fetch(`${backendUrl}/api/clubs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(clubPayload)
      });

      if (!clubResponse.ok) {
        const errorData = await clubResponse.json().catch(() => ({}));
        console.error('Club creation error response:', errorData);
        throw new Error(errorData.message || errorData.error || 'Failed to create club');
      }

      const newClub = await clubResponse.json();
      setCreatedClub(newClub);

      // 2. Create Categories (in parallel)
      const enabledCategories = formData.categories.filter(cat => cat.enabled);
      
      await Promise.all(
        enabledCategories.map(cat =>
          fetch(`${backendUrl}/api/categories`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              name: cat.name,
              description: cat.description,
              clubId: newClub._id,
              createdBy: userProfile?._id
            })
          })
        )
      );

      // 3. Update notification category settings with user's preferences
      try {
        const notifSettings = formData.notifications;
        
        // Update each category's enabled status
        // Backend expects: { "enabled": boolean }
        const categoryNames = ['program', 'event', 'rsvp', 'equipment', 'team', 'assignment'];
        
        for (const category of categoryNames) {
          const isEnabled = notifSettings.categories?.[category] ?? true;
          await fetch(`${backendUrl}/api/clubs/${newClub._id}/notification-settings/category/${category}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              enabled: isEnabled
            })
          });
        }
        
        console.log('Notification settings saved successfully');
      } catch (notifError) {
        console.warn('Could not update notification settings:', notifError);
        // Don't fail club creation if notification update fails
      }

      // 4. Refresh user clubs
      refreshUserClubs();

      // 5. Move to completion step
      setCurrentStep(5);
      showSuccess('Club created successfully!');

    } catch (error) {
      console.error('Error creating club:', error);
      showError(error.message || 'Failed to create club. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle completion actions
  const handleViewClub = () => {
    if (createdClub?._id) {
      onComplete?.(createdClub);
      onClose();
      // Navigation will be handled by parent
    }
  };

  const handleCreateAnother = () => {
    // Reset wizard
    setCurrentStep(1);
    setCreatedClub(null);
    setFormData({
      name: '',
      sport: '',
      type: 'Club',
      businessType: 'Public club',
      description: '',
      membershipType: 'free_open',
      membershipFee: 0,
      clubLogo: null,
      // Branding fields
      bannerImage: null,
      primaryColor: null,
      tagline: null,
      socialLinks: {
        website: null,
        facebook: null,
        instagram: null,
        twitter: null,
        youtube: null
      },
      clubType: 'Physical Location',
      contactNumber: '',
      email: '',
      street: '',
      city: '',
      state: '',
      country: '',
      zipCode: '',
      location: userLocation ? {
        type: 'Point',
        coordinates: [userLocation.lng, userLocation.lat]
      } : null,
      categories: [...DEFAULT_CATEGORIES],
      notifications: {
        channels: { inApp: true, email: true },
        categories: {
          program: true,
          event: true,
          rsvp: true,
          equipment: true,
          team: true,
          assignment: true
        }
      }
    });
  };

  const handleClose = () => {
    if (createdClub) {
      onComplete?.(createdClub);
    }
    onClose();
  };

  // Render current step
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <ClubDetailsStep
            formData={formData}
            updateFormData={updateFormData}
            sportsOptions={sportNames}
            sportsData={sports}
            sportsLoading={sportsLoading}
            onNext={handleNext}
            onClose={handleClose}
          />
        );
      
      case 2:
        return (
          <ContactInfoStep
            formData={formData}
            updateFormData={updateFormData}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      
      case 3:
        return (
          <CategoriesStep
            formData={formData}
            updateFormData={updateFormData}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      
      case 4:
        return (
          <NotificationsStep
            formData={formData}
            updateFormData={updateFormData}
            onSubmit={handleSubmit}
            onBack={handleBack}
            isSubmitting={isSubmitting}
          />
        );
      
      case 5:
        return (
          <CompletionStep
            club={createdClub}
            categoriesCount={formData.categories.filter(c => c.enabled).length}
            onViewClub={handleViewClub}
            onCreateAnother={handleCreateAnother}
            onClose={handleClose}
          />
        );
      
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className={styles.overlay}>
      <div className={styles.wizard}>
        {/* Progress Indicator (hide on completion screen) */}
        {currentStep < 5 && (
          <ClubCreationProgress
            currentStep={currentStep}
            totalSteps={4}
          />
        )}

        {/* Step Content */}
        <div className={styles.stepContent}>
          {renderStep()}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ClubCreationWizard;

